/**
 * Login screen - split-screen layout with brand panel + auth card.
 *
 * Stages:
 *   1. credentials       - email + password
 *   2. new-password      - first-time users must replace the temp pw
 *   3. mfa-setup         - TOTP QR + verify code (first login only)
 *   4. mfa-challenge     - ongoing 6-digit TOTP challenge
 *   5. forgot-email      - enter email to receive reset code
 *   6. forgot-confirm    - code + new password
 *   7. forgot-done       - success → back to sign-in
 *
 * On SUCCESS, redirect to `/`. The profile query on / picks up the
 * session via AdminClient → CognitoTokenStore.
 */

import { Eye, EyeOff, KeyRound, Lock, Mail, ShieldCheck } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  completeNewPassword,
  confirmForgotPassword,
  forgotPassword,
  signIn,
  verifyTotpChallenge,
  verifyTotpSetup,
  type LoginChallenge,
} from "@/lib/auth";
import { usePageTitle } from "@/lib/use-page-title";
import { cn } from "@/lib/utils";

type Stage =
  | { kind: "credentials" }
  | { kind: "new-password"; challenge: Extract<LoginChallenge, { type: "NEW_PASSWORD_REQUIRED" }> }
  | { kind: "mfa-setup"; challenge: Extract<LoginChallenge, { type: "MFA_SETUP" }> }
  | { kind: "mfa-challenge"; challenge: Extract<LoginChallenge, { type: "SOFTWARE_TOKEN_MFA" }> }
  | { kind: "forgot-email" }
  | { kind: "forgot-confirm"; email: string; destination: string }
  | { kind: "forgot-done" };

export function LoginPage(): JSX.Element {
  usePageTitle("Sign in");
  const [stage, setStage] = useState<Stage>({ kind: "credentials" });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  async function handleCredentials(email: string, password: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const result = await signIn(email, password);
      if (result.status === "ERROR") {
        setError(result.error.message);
        return;
      }
      if (result.status === "SUCCESS") {
        navigate("/", { replace: true });
        return;
      }
      switch (result.challenge.type) {
        case "NEW_PASSWORD_REQUIRED":
          setStage({ kind: "new-password", challenge: result.challenge });
          break;
        case "MFA_SETUP":
          setStage({ kind: "mfa-setup", challenge: result.challenge });
          break;
        case "SOFTWARE_TOKEN_MFA":
          setStage({ kind: "mfa-challenge", challenge: result.challenge });
          break;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleNewPassword(newPassword: string): Promise<void> {
    if (stage.kind !== "new-password") return;
    setBusy(true);
    setError(null);
    try {
      const result = await completeNewPassword(
        stage.challenge.user,
        newPassword,
        stage.challenge.userAttributes,
      );
      if (result.status === "ERROR") {
        setError(result.error.message);
        return;
      }
      if (result.status === "SUCCESS") {
        navigate("/", { replace: true });
        return;
      }
      if (result.challenge.type === "MFA_SETUP") {
        setStage({ kind: "mfa-setup", challenge: result.challenge });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleMfaSetup(code: string): Promise<void> {
    if (stage.kind !== "mfa-setup") return;
    setBusy(true);
    setError(null);
    try {
      const result = await verifyTotpSetup(stage.challenge.user, code);
      if (result.status === "ERROR") {
        setError(result.error.message);
        return;
      }
      if (result.status === "SUCCESS") {
        navigate("/", { replace: true });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleMfaChallenge(code: string): Promise<void> {
    if (stage.kind !== "mfa-challenge") return;
    setBusy(true);
    setError(null);
    try {
      const result = await verifyTotpChallenge(stage.challenge.user, code);
      if (result.status === "ERROR") {
        setError(result.error.message);
        return;
      }
      if (result.status === "SUCCESS") {
        navigate("/", { replace: true });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleForgotRequest(email: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const result = await forgotPassword(email);
      if (result.status === "ERROR") {
        setError(result.error.message);
        return;
      }
      setStage({ kind: "forgot-confirm", email, destination: result.destination });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleForgotConfirm(code: string, newPassword: string): Promise<void> {
    if (stage.kind !== "forgot-confirm") return;
    setBusy(true);
    setError(null);
    try {
      const result = await confirmForgotPassword(stage.email, code, newPassword);
      if (result.status === "ERROR") {
        setError(result.error.message);
        return;
      }
      setStage({ kind: "forgot-done" });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  const heading = stageHeading(stage.kind);

  return (
    <main className="grid min-h-screen grid-cols-1 bg-background text-foreground md:grid-cols-5">
      {/* Brand panel - hides on mobile so the form gets full width. */}
      <aside className="relative hidden overflow-hidden bg-primary text-primary-foreground md:col-span-2 md:block">
        <div
          className="absolute inset-0 opacity-90"
          style={{
            background:
              "radial-gradient(1200px 800px at 10% 10%, hsl(221 90% 60%) 0%, hsl(221 83% 43%) 40%, hsl(222 84% 18%) 100%)",
          }}
        />
        <div className="relative flex h-full flex-col justify-between p-10">
          <div className="flex items-center gap-3">
            <img
              src="/finterra-logo.jpg"
              alt=""
              className="h-12 w-12 rounded-md bg-white object-contain p-1 shadow-sm"
            />
            <span className="text-lg font-semibold tracking-tight">Finterra Admin</span>
          </div>
          <div className="space-y-4">
            <h2 className="text-3xl font-semibold tracking-tight">
              Payments infrastructure, simplified.
            </h2>
            <p className="max-w-md text-sm leading-relaxed text-white/80">
              Manage your organizations, locations, partners, and integrations from one place.
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs text-white/70">
            <ShieldCheck className="h-4 w-4" />
            <span>Secured with TOTP multi-factor auth on every admin session.</span>
          </div>
        </div>
      </aside>

      {/* Form card.
       * Mobile: stack from the top with comfortable padding - no
       *         vertical centering, which was dropping the card halfway
       *         down tall viewports and leaving visual dead space.
       * md+:    centre vertically inside the right column (brand panel
       *         on the left takes the height cue). */}
      <section className="flex flex-col items-center px-4 pb-12 pt-8 sm:px-6 sm:pt-12 md:col-span-3 md:min-h-screen md:justify-center md:pb-6 md:pt-6">
        <div className="w-full max-w-md">
          {/* Compact logo for mobile, hidden on desktop (brand panel has it). */}
          <div className="mb-6 flex justify-center md:hidden">
            <img src="/finterra-logo.jpg" alt="Finterra" className="h-14 w-auto" />
          </div>
          {/* On narrow phones the card's border + shadow feels boxed-in;
              drop them below sm so the form reads edge-to-edge. Restore
              the card treatment at sm+ where screen real-estate allows. */}
          <div className="bg-card p-0 sm:rounded-xl sm:border sm:p-8 sm:shadow-sm">
            <h1 className="mb-1 text-2xl font-semibold tracking-tight">{heading.title}</h1>
            <p className="mb-6 text-sm text-muted-foreground">{heading.subtitle}</p>

            {stage.kind === "credentials" && (
              <CredentialsForm
                busy={busy}
                onSubmit={handleCredentials}
                onForgot={() => {
                  setError(null);
                  setStage({ kind: "forgot-email" });
                }}
              />
            )}
            {stage.kind === "new-password" && (
              <NewPasswordForm busy={busy} onSubmit={handleNewPassword} />
            )}
            {stage.kind === "mfa-setup" && (
              <MfaSetupForm
                busy={busy}
                qrUri={stage.challenge.qrUri}
                secret={stage.challenge.secretCode}
                onSubmit={handleMfaSetup}
              />
            )}
            {stage.kind === "mfa-challenge" && (
              <MfaChallengeForm busy={busy} onSubmit={handleMfaChallenge} />
            )}
            {stage.kind === "forgot-email" && (
              <ForgotEmailForm
                busy={busy}
                onSubmit={handleForgotRequest}
                onCancel={() => {
                  setError(null);
                  setStage({ kind: "credentials" });
                }}
              />
            )}
            {stage.kind === "forgot-confirm" && (
              <ForgotConfirmForm
                busy={busy}
                destination={stage.destination}
                onSubmit={handleForgotConfirm}
                onResend={() => void handleForgotRequest(stage.email)}
              />
            )}
            {stage.kind === "forgot-done" && (
              <ForgotDone onBackToSignIn={() => setStage({ kind: "credentials" })} />
            )}

            {error && (
              <div className="mt-4 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                <svg
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className="mt-0.5 h-4 w-4 flex-shrink-0"
                  aria-hidden
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm-1-11a1 1 0 112 0v4a1 1 0 11-2 0V7zm1 7a1 1 0 100 2 1 1 0 000-2z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>{error}</span>
              </div>
            )}
          </div>

          <p className="mt-6 text-center text-xs text-muted-foreground">
            Need help? Contact your workspace administrator.
          </p>
        </div>
      </section>
    </main>
  );
}

function stageHeading(kind: Stage["kind"]): { title: string; subtitle: string } {
  switch (kind) {
    case "credentials":
      return { title: "Welcome back", subtitle: "Sign in to your Finterra admin account." };
    case "new-password":
      return {
        title: "Set a new password",
        subtitle: "Your temporary password must be replaced before you can sign in.",
      };
    case "mfa-setup":
      return {
        title: "Set up two-factor auth",
        subtitle: "Scan the code with Google Authenticator, 1Password, Authy, or any TOTP app.",
      };
    case "mfa-challenge":
      return {
        title: "Enter your verification code",
        subtitle: "Open your authenticator app and enter the current 6-digit code.",
      };
    case "forgot-email":
      return {
        title: "Forgot your password?",
        subtitle: "Enter your email and we'll send you a reset code.",
      };
    case "forgot-confirm":
      return {
        title: "Check your email",
        subtitle: "Enter the code we just sent along with your new password.",
      };
    case "forgot-done":
      return {
        title: "Password updated",
        subtitle: "You can now sign in with your new password.",
      };
  }
}

// --------------------------------------------------------------------- //
// Sub-forms
// --------------------------------------------------------------------- //

function CredentialsForm(props: {
  busy: boolean;
  onSubmit: (email: string, password: string) => void;
  onForgot: () => void;
}): JSX.Element {
  const savedEmail = localStorage.getItem("login_remembered_email") ?? "";
  const [email, setEmail] = useState(savedEmail);
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(savedEmail !== "");
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (rememberMe) {
          localStorage.setItem("login_remembered_email", email);
        } else {
          localStorage.removeItem("login_remembered_email");
        }
        props.onSubmit(email, password);
      }}
      className="space-y-4"
    >
      <FieldWithIcon
        label="Email"
        icon={<Mail className="h-4 w-4" />}
        inputProps={{
          type: "email",
          required: true,
          autoFocus: !savedEmail,
          autoComplete: "username",
          value: email,
          onChange: (e) => setEmail(e.target.value),
        }}
      />
      <div>
        <PasswordFieldWithIcon
          label="Password"
          icon={<Lock className="h-4 w-4" />}
          inputProps={{
            required: true,
            autoFocus: !!savedEmail,
            autoComplete: "current-password",
            value: password,
            onChange: (e) => setPassword(e.target.value),
          }}
        />
      </div>
      <div className="flex items-center justify-between">
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            className="h-4 w-4 rounded border-border accent-primary"
          />
          <span className="text-muted-foreground">Remember email</span>
        </label>
        <button
          type="button"
          onClick={props.onForgot}
          className="text-xs text-primary hover:underline"
        >
          Forgot password?
        </button>
      </div>
      <PrimaryButton busy={props.busy} busyLabel="Signing in…" label="Sign in" />
    </form>
  );
}

function NewPasswordForm(props: {
  busy: boolean;
  onSubmit: (newPassword: string) => void;
}): JSX.Element {
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [localErr, setLocalErr] = useState<string | null>(null);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (next !== confirm) {
          setLocalErr("Passwords do not match");
          return;
        }
        if (next.length < 14) {
          setLocalErr("Password must be at least 14 characters");
          return;
        }
        setLocalErr(null);
        props.onSubmit(next);
      }}
      className="space-y-4"
    >
      <p className="rounded-md bg-muted p-3 text-xs text-muted-foreground">
        Pick something <strong>14+ characters</strong> with symbols, numbers, upper + lower case.
      </p>
      <PasswordFieldWithIcon
        label="New password"
        icon={<Lock className="h-4 w-4" />}
        inputProps={{
          required: true,
          autoFocus: true,
          autoComplete: "new-password",
          value: next,
          onChange: (e) => setNext(e.target.value),
        }}
      />
      <PasswordFieldWithIcon
        label="Confirm password"
        icon={<Lock className="h-4 w-4" />}
        inputProps={{
          required: true,
          autoComplete: "new-password",
          value: confirm,
          onChange: (e) => setConfirm(e.target.value),
        }}
      />
      {localErr && <div className="text-sm text-destructive">{localErr}</div>}
      <PrimaryButton busy={props.busy} busyLabel="Setting password…" label="Set password" />
    </form>
  );
}

function MfaSetupForm(props: {
  busy: boolean;
  qrUri: string;
  secret: string;
  onSubmit: (code: string) => void;
}): JSX.Element {
  const [code, setCode] = useState("");
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        props.onSubmit(code);
      }}
      className="space-y-4"
    >
      <div className="flex justify-center rounded-lg border bg-white p-4">
        <QRCodeSVG value={props.qrUri} size={192} marginSize={1} />
      </div>
      <details className="text-xs">
        <summary className="cursor-pointer text-muted-foreground">
          Can't scan? Enter this secret manually.
        </summary>
        <code className="mt-2 block break-words rounded bg-muted p-2 font-mono text-[11px]">
          {props.secret}
        </code>
      </details>
      <FieldWithIcon
        label="Verification code"
        icon={<KeyRound className="h-4 w-4" />}
        inputProps={{
          inputMode: "numeric",
          required: true,
          autoFocus: true,
          autoComplete: "one-time-code",
          pattern: "[0-9]{6}",
          maxLength: 6,
          placeholder: "123 456",
          value: code,
          onChange: (e) => setCode(e.target.value),
        }}
      />
      <PrimaryButton busy={props.busy} busyLabel="Verifying…" label="Verify + enable MFA" />
    </form>
  );
}

function MfaChallengeForm(props: { busy: boolean; onSubmit: (code: string) => void }): JSX.Element {
  const [code, setCode] = useState("");
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        props.onSubmit(code);
      }}
      className="space-y-4"
    >
      <FieldWithIcon
        label="6-digit code"
        icon={<KeyRound className="h-4 w-4" />}
        inputProps={{
          inputMode: "numeric",
          required: true,
          autoFocus: true,
          autoComplete: "one-time-code",
          pattern: "[0-9]{6}",
          maxLength: 6,
          placeholder: "123 456",
          value: code,
          onChange: (e) => setCode(e.target.value),
        }}
      />
      <PrimaryButton busy={props.busy} busyLabel="Verifying…" label="Verify" />
    </form>
  );
}

function ForgotEmailForm(props: {
  busy: boolean;
  onSubmit: (email: string) => void;
  onCancel: () => void;
}): JSX.Element {
  const [email, setEmail] = useState("");
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        props.onSubmit(email);
      }}
      className="space-y-4"
    >
      <FieldWithIcon
        label="Email"
        icon={<Mail className="h-4 w-4" />}
        inputProps={{
          type: "email",
          required: true,
          autoFocus: true,
          autoComplete: "username",
          value: email,
          onChange: (e) => setEmail(e.target.value),
        }}
      />
      <PrimaryButton busy={props.busy} busyLabel="Sending…" label="Send reset code" />
      <button
        type="button"
        onClick={props.onCancel}
        className="w-full text-center text-xs text-muted-foreground hover:underline"
      >
        Back to sign-in
      </button>
    </form>
  );
}

function ForgotConfirmForm(props: {
  busy: boolean;
  destination: string;
  onSubmit: (code: string, newPassword: string) => void;
  onResend: () => void;
}): JSX.Element {
  const [code, setCode] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [localErr, setLocalErr] = useState<string | null>(null);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (next !== confirm) {
          setLocalErr("Passwords do not match");
          return;
        }
        if (next.length < 14) {
          setLocalErr("Password must be at least 14 characters");
          return;
        }
        setLocalErr(null);
        props.onSubmit(code, next);
      }}
      className="space-y-4"
    >
      <p className="rounded-md bg-muted p-3 text-xs text-muted-foreground">
        A 6-digit code was sent to <strong>{props.destination}</strong>.
      </p>
      <FieldWithIcon
        label="Code"
        icon={<KeyRound className="h-4 w-4" />}
        inputProps={{
          inputMode: "numeric",
          required: true,
          autoFocus: true,
          autoComplete: "one-time-code",
          maxLength: 6,
          pattern: "[0-9]{6}",
          value: code,
          onChange: (e) => setCode(e.target.value),
        }}
      />
      <PasswordFieldWithIcon
        label="New password"
        icon={<Lock className="h-4 w-4" />}
        inputProps={{
          required: true,
          autoComplete: "new-password",
          value: next,
          onChange: (e) => setNext(e.target.value),
        }}
      />
      <PasswordFieldWithIcon
        label="Confirm new password"
        icon={<Lock className="h-4 w-4" />}
        inputProps={{
          required: true,
          autoComplete: "new-password",
          value: confirm,
          onChange: (e) => setConfirm(e.target.value),
        }}
      />
      {localErr && <div className="text-sm text-destructive">{localErr}</div>}
      <PrimaryButton busy={props.busy} busyLabel="Resetting…" label="Reset password" />
      <button
        type="button"
        onClick={props.onResend}
        className="w-full text-center text-xs text-primary hover:underline"
      >
        Re-send code
      </button>
    </form>
  );
}

function ForgotDone(props: { onBackToSignIn: () => void }): JSX.Element {
  return (
    <div className="space-y-4">
      <div className="rounded-md bg-primary/5 p-3 text-sm text-primary">
        Your password has been updated. You can sign in now.
      </div>
      <button type="button" onClick={props.onBackToSignIn} className={primaryButtonCls}>
        Back to sign-in
      </button>
    </div>
  );
}

// --------------------------------------------------------------------- //
// Shared pieces
// --------------------------------------------------------------------- //

function FieldWithIcon(props: {
  label: string;
  icon: React.ReactNode;
  inputProps: React.InputHTMLAttributes<HTMLInputElement>;
}): JSX.Element {
  return (
    <label className="block text-sm">
      <span className="mb-1 block font-medium text-foreground">{props.label}</span>
      <div className="relative">
        <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-muted-foreground">
          {props.icon}
        </span>
        <input
          {...props.inputProps}
          className={cn(
            "w-full rounded-md border bg-background py-2 pl-10 pr-3 text-sm outline-none transition",
            "focus:border-primary focus:ring-2 focus:ring-primary/20",
          )}
        />
      </div>
    </label>
  );
}

function PasswordFieldWithIcon(props: {
  label: string;
  icon: React.ReactNode;
  inputProps: Omit<React.InputHTMLAttributes<HTMLInputElement>, "type">;
}): JSX.Element {
  const [show, setShow] = useState(false);
  return (
    <label className="block text-sm">
      <span className="mb-1 block font-medium text-foreground">{props.label}</span>
      <div className="relative">
        <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-muted-foreground">
          {props.icon}
        </span>
        <input
          {...props.inputProps}
          type={show ? "text" : "password"}
          className={cn(
            "w-full rounded-md border bg-background py-2 pl-10 pr-9 text-sm outline-none transition",
            "focus:border-primary focus:ring-2 focus:ring-primary/20",
          )}
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          className="absolute inset-y-0 right-2 flex items-center text-muted-foreground hover:text-foreground"
          aria-label={show ? "Hide password" : "Show password"}
        >
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </label>
  );
}

function PrimaryButton(props: { busy: boolean; busyLabel: string; label: string }): JSX.Element {
  return (
    <button type="submit" disabled={props.busy} className={primaryButtonCls}>
      {props.busy ? props.busyLabel : props.label}
    </button>
  );
}

const primaryButtonCls =
  "w-full rounded-md bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60";
